/**
 * Task step types, step label maps, and step derivation logic.
 * Extracted from taskSteps.tsx for modularity.
 */
import React from "react";

import BuildIcon from "@mui/icons-material/Build";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CodeIcon from "@mui/icons-material/Code";
import DescriptionIcon from "@mui/icons-material/Description";
import ErrorIcon from "@mui/icons-material/Error";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import ScienceIcon from "@mui/icons-material/Science";
import SyncIcon from "@mui/icons-material/Sync";

import type { DeploymentEvent } from "src/domains/application/hooks/useDeploymentEvents";
import type { BuildLogJob } from "src/domains/application/module/api/IApplicationApi";

import { formatEventType } from "./shared/pipeline-helpers";

export { formatEventType };

// ── Types ─────────────────────────────────────────────────────────────────────

export interface TaskStep {
  key: string;
  label: string;
  icon: React.ReactElement;
  status:
    "completed" | "in_progress" | "failed" | "pending" | "queued" | "skipped";
  startedAt?: string | null;
  completedAt?: string | null;
  logLines?: string[];
  htmlUrl?: string;
}

// ── Step Label Maps ───────────────────────────────────────────────────────────

export const BUILD_PHASE_STEPS: Record<
  string,
  { label: string; icon: React.ReactElement }
> = {
  checkout: { label: "Checkout source", icon: <CodeIcon /> },
  install: { label: "Install dependencies", icon: <BuildIcon /> },
  build: { label: "Build image", icon: <BuildIcon /> },
  push: { label: "Push to registry", icon: <CloudUploadIcon /> },
};

export const DEPLOY_PHASE_STEPS: Record<
  string,
  { label: string; icon: React.ReactElement }
> = {
  render: { label: "Render manifests", icon: <DescriptionIcon /> },
  commit: { label: "Commit to GitOps", icon: <CodeIcon /> },
  sync: { label: "Sync ArgoCD", icon: <SyncIcon /> },
  verify: { label: "Verify health", icon: <CheckCircleIcon /> },
};

export const CI_JOB_STEPS: Record<
  string,
  { label: string; icon: React.ReactElement }
> = {
  lint: { label: "Lint", icon: <CodeIcon /> },
  test: { label: "Run tests", icon: <ScienceIcon /> },
  "type-check": { label: "Type check", icon: <CodeIcon /> },
  build: { label: "Build", icon: <BuildIcon /> },
};

export const EVENT_STEP_MAP: Record<
  string,
  { label: string; icon: React.ReactElement; useNameFromMeta?: boolean }
> = {
  // Build events
  deployment_build_started: {
    label: "Build triggered",
    icon: <RocketLaunchIcon />,
  },
  deployment_build_completed: {
    label: "Build completed",
    icon: <CheckCircleIcon />,
  },
  deployment_build_failed: { label: "Build failed", icon: <ErrorIcon /> },
  deployment_build_service_started: {
    label: "Build",
    icon: <BuildIcon />,
    useNameFromMeta: true,
  },
  deployment_build_service_completed: {
    label: "Build",
    icon: <CheckCircleIcon />,
    useNameFromMeta: true,
  },
  deployment_build_service_failed: {
    label: "Build",
    icon: <ErrorIcon />,
    useNameFromMeta: true,
  },
  // Deploy events
  deployment_deploy_started: {
    label: "Deploy triggered",
    icon: <RocketLaunchIcon />,
  },
  deployment_deploy_queued: {
    label: "Deploy Queued",
    icon: <HourglassEmptyIcon />,
  },
  deployment_deploy_k8s_apply_started: {
    label: "Apply Kubernetes manifests",
    icon: <DescriptionIcon />,
  },
  deployment_deploy_completed: {
    label: "Deploy completed",
    icon: <CheckCircleIcon />,
  },
  deployment_deploy_failed: { label: "Deploy failed", icon: <ErrorIcon /> },
  deployment_deploy_service_started: {
    label: "Deploy",
    icon: <RocketLaunchIcon />,
    useNameFromMeta: true,
  },
  deployment_deploy_service_completed: {
    label: "Deploy",
    icon: <CheckCircleIcon />,
    useNameFromMeta: true,
  },
  deployment_deploy_service_failed: {
    label: "Deploy",
    icon: <ErrorIcon />,
    useNameFromMeta: true,
  },
  // VCS events
  deployment_vcs_provisioning_started: {
    label: "Provision repository",
    icon: <CodeIcon />,
  },
  deployment_vcs_provisioning_completed: {
    label: "Repository ready",
    icon: <CheckCircleIcon />,
  },
  deployment_vcs_provisioning_failed: {
    label: "Provision failed",
    icon: <ErrorIcon />,
  },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

export function formatStepDuration(
  startedAt?: string | null,
  completedAt?: string | null,
): string | null {
  if (!startedAt) return null;
  const start = new Date(startedAt).getTime();
  const end = completedAt ? new Date(completedAt).getTime() : Date.now();
  const sec = Math.round((end - start) / 1000);
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

// ── Step Derivation Functions ─────────────────────────────────────────────────

export function deriveLiveLogSteps(
  liveLogs: Map<string, string[]>,
  step: string,
): TaskStep[] {
  const phaseMap = step === "deploy" ? DEPLOY_PHASE_STEPS : BUILD_PHASE_STEPS;
  const steps: TaskStep[] = [];

  for (const [phase, lines] of liveLogs) {
    const config = phaseMap[phase] ?? {
      label: phase.charAt(0).toUpperCase() + phase.slice(1),
      icon: <BuildIcon />,
    };
    const hasError = lines.some(
      (l) =>
        l.toLowerCase().includes("error") || l.toLowerCase().includes("failed"),
    );
    const isRunning = lines.some(
      (l) =>
        l.toLowerCase().includes("running") ||
        l.toLowerCase().includes("building"),
    );
    let status: TaskStep["status"] = "completed";
    if (hasError) status = "failed";
    else if (isRunning) status = "in_progress";
    else if (lines.length === 0) status = "pending";

    steps.push({
      key: phase,
      label: config.label,
      icon: config.icon,
      status,
      logLines: lines,
    });
  }
  return steps;
}

export function deriveCIJobSteps(
  buildLogJobs: BuildLogJob[],
  qualityGates?: BuildLogJob[],
): TaskStep[] {
  const steps: TaskStep[] = [];

  if (qualityGates && qualityGates.length > 0) {
    for (const job of qualityGates) {
      const config = CI_JOB_STEPS[job.jobName] ?? {
        label:
          job.jobName.charAt(0).toUpperCase() +
          job.jobName.slice(1).replace(/[-_]/g, " "),
        icon: <CodeIcon />,
      };
      steps.push({
        key: `qg-${job.jobName}`,
        label: config.label,
        icon: config.icon,
        status:
          job.conclusion === "success"
            ? "completed"
            : job.conclusion === "failure"
              ? "failed"
              : job.status === "in_progress"
                ? "in_progress"
                : job.status === "queued"
                  ? "queued"
                  : "pending",
        startedAt: job.startedAt,
        completedAt: job.completedAt,
        logLines: job.logLines,
        htmlUrl: job.htmlUrl,
      });
    }
  }

  for (const job of buildLogJobs) {
    if (qualityGates?.some((qg) => qg.jobName === job.jobName)) continue;
    const config = CI_JOB_STEPS[job.jobName] ??
      BUILD_PHASE_STEPS[job.jobName] ?? {
        label:
          job.jobName.charAt(0).toUpperCase() +
          job.jobName.slice(1).replace(/[-_]/g, " "),
        icon: <BuildIcon />,
      };
    steps.push({
      key: `ci-${job.jobName}`,
      label: config.label,
      icon: config.icon,
      status:
        job.conclusion === "success"
          ? "completed"
          : job.conclusion === "failure"
            ? "failed"
            : job.status === "in_progress"
              ? "in_progress"
              : job.status === "queued"
                ? "queued"
                : "pending",
      startedAt: job.startedAt,
      completedAt: job.completedAt,
      logLines: job.logLines,
      htmlUrl: job.htmlUrl,
    });
  }

  return steps;
}

/**
 * Create a build step from pipeline state data.
 * Used for completed rusha_managed builds where the Kaniko build log
 * is no longer available via liveLogs but we have startedAt/completedAt/status.
 */
export function deriveServiceBuildStep(job: {
  name: string;
  status: string;
  startedAt: string | null;
  completedAt: string | null;
  message?: string;
}): TaskStep {
  const logLines: string[] = [];
  if (job.message && job.message !== job.status) {
    logLines.push(job.message);
  }
  return {
    key: `build-${job.name}`,
    label: `Build ${job.name}`,
    icon: <BuildIcon />,
    status:
      job.status === "completed"
        ? "completed"
        : job.status === "failed"
          ? "failed"
          : job.status === "in_progress"
            ? "in_progress"
            : "pending",
    startedAt: job.startedAt,
    completedAt: job.completedAt,
    logLines: logLines.length > 0 ? logLines : undefined,
  };
}

export function deriveEventSteps(events: DeploymentEvent[]): TaskStep[] {
  const seen = new Map<string, DeploymentEvent>();
  for (const ev of events) {
    const svc = (ev.metadata?.serviceName as string) ?? "";
    const key = `${ev.eventType}:${svc}`;
    if (!seen.has(key) || ev.createdAt > seen.get(key)!.createdAt) {
      seen.set(key, ev);
    }
  }

  const steps: TaskStep[] = [];
  for (const ev of seen.values()) {
    const config = EVENT_STEP_MAP[ev.eventType];
    const svcName = ev.metadata?.serviceName as string | undefined;

    const label = config
      ? config.useNameFromMeta && svcName
        ? `${config.label} ${svcName}`
        : config.label
      : formatEventType(ev.eventType);
    const icon = config ? config.icon : <CodeIcon />;

    const logLines: string[] = [];
    if (ev.severity === "error" && typeof ev.metadata?.error === "string") {
      logLines.push(ev.metadata.error as string);
    }
    if (ev.message && ev.message !== ev.status) {
      logLines.push(ev.message);
    }

    const hasRealStartedAt = ev.startedAt != null && ev.startedAt !== "";
    const hasRealCompletedAt = ev.completedAt != null && ev.completedAt !== "";

    steps.push({
      key: ev.id,
      label,
      icon,
      status:
        ev.status === "completed"
          ? "completed"
          : ev.status === "failed"
            ? "failed"
            : ev.status === "in_progress"
              ? "in_progress"
              : "pending",
      startedAt: hasRealStartedAt ? ev.startedAt : undefined,
      completedAt: hasRealCompletedAt ? ev.completedAt : undefined,
      logLines: logLines.length > 0 ? logLines : undefined,
    });
  }

  steps.sort(
    (a, b) =>
      new Date(a.startedAt ?? 0).getTime() -
      new Date(b.startedAt ?? 0).getTime(),
  );
  return steps;
}
